import { z } from 'zod'

/**
 * Wire schemas for managerium's `/v1/dashboards` endpoints (see managerium's
 * docs/adr/v02/openapi/openapi.yaml). Only dashboard 593 is used so far.
 */

const cardStubSchema = z.object({
  cardId: z.number(),
  name: z.string(),
  display: z.string(),
  /** What the metric means, for the card's (i) tooltip. */
  description: z.string().nullish(),
  /** The deep-dive section this card links to, when the backend picked one. */
  deepDiveId: z.string().nullish(),
})

export type CardStub = z.infer<typeof cardStubSchema>

const sectionKindSchema = z.enum(['section', 'deep_dive'])

export type DashboardSection = {
  id: string
  title: string
  kind: z.infer<typeof sectionKindSchema>
  cards: CardStub[]
  subsections?: DashboardSection[] | undefined
}

/**
 * Bump when a Managerium response changes shape. Cached responses are kept
 * across reloads (see shared/api/queryClient.ts) and are discarded when this
 * changes, so nobody is served the old shape.
 */
export const CONTRACT_VERSION = '3'

const dashboardSectionSchema: z.ZodType<DashboardSection> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    kind: sectionKindSchema,
    cards: z.array(cardStubSchema),
    subsections: z.array(dashboardSectionSchema).optional(),
  })
)

const periodOptionSchema = z.object({ value: z.number(), label: z.string() })

const marketSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** A country code for the flag (`de`), when the market has one. */
  flag: z.string().nullish(),
})

/** The dashboard-wide filters: the range of months and the markets to show. */
const dashboardFiltersSchema = z.object({
  periods: z.object({
    default: z.number(),
    options: z.array(periodOptionSchema),
  }),
  markets: z.array(marketSchema),
})

export type DashboardFilters = z.infer<typeof dashboardFiltersSchema>

export const dashboardStructureSchema = z.object({
  dashboardId: z.number(),
  instance: z.string(),
  name: z.string(),
  lastUpdated: z.string().nullish(),
  sections: z.array(dashboardSectionSchema),
  filters: dashboardFiltersSchema.optional(),
})

export type DashboardStructure = z.infer<typeof dashboardStructureSchema>

const trendDirectionSchema = z.enum(['up', 'down', 'flat'])
export type TrendDirection = z.infer<typeof trendDirectionSchema>

/** How a card's numbers are shown; percent cards hold raw fractions (0.12 = 12%). */
const cardFormatSchema = z.object({
  style: z.enum(['number', 'currency', 'percent']),
  decimals: z.number().nullable(),
})

export type CardFormat = z.infer<typeof cardFormatSchema>

const defaultFormat: CardFormat = { style: 'number', decimals: null }

const kpiCardSchema = z.object({
  kind: z.literal('kpi'),
  cardId: z.number(),
  name: z.string(),
  unit: z.string().nullish(),
  format: cardFormatSchema.default(defaultFormat),
  value: z.number(),
  previousValue: z.number().nullish(),
  trendDirection: trendDirectionSchema,
})

/** The chart displays Managerium's Metabase mapper accepts. */
export const chartTypes = [
  'area',
  'line',
  'bar',
  'waterfall',
  'pie',
  'scatter',
  'combo',
  'table',
] as const

const chartCardSchema = z.object({
  kind: z.literal('chart'),
  cardId: z.number(),
  name: z.string(),
  /**
   * Managerium forwards Metabase's `display`. The UI draws `bar` as bars and
   * every other type as a line for now. A type Managerium doesn't list here
   * fails the parse loudly (an error tile) instead of being drawn wrongly.
   */
  chartType: z.enum(chartTypes),
  unit: z.string().nullish(),
  format: cardFormatSchema.default(defaultFormat),
  /** Headline: the sum of all series at the latest / previous category. */
  value: z.number().nullish(),
  previousValue: z.number().nullish(),
  /** Per-category sum of all series, aligned with `categories`. */
  totals: z.array(z.number().nullable()).default([]),
  categories: z.array(z.string()),
  series: z.array(
    z.object({
      name: z.string(),
      data: z.array(z.number().nullable()),
    })
  ),
  /** The range of months the series was sliced to. */
  period: z.object({ periods: z.number(), label: z.string() }).optional(),
  /** The global target, drawn as a line across the expanded chart. */
  goalValue: z.number().nullish(),
})

/** What a card shows instead when the market filter leaves nothing to show. */
const emptyReasonSchema = z.enum(['market-not-available', 'no-rows'])
export type EmptyReason = z.infer<typeof emptyReasonSchema>

const emptyCardSchema = z.object({
  kind: z.literal('empty'),
  cardId: z.number(),
  name: z.string(),
  reason: emptyReasonSchema,
})

export const cardSchema = z.discriminatedUnion('kind', [
  kpiCardSchema,
  chartCardSchema,
  emptyCardSchema,
])

export type Card = z.infer<typeof cardSchema>
export type ChartCard = z.infer<typeof chartCardSchema>
